import { ApolloProvider } from '@apollo/client';
import { FlagsProvider } from '@atlaskit/flag';
import { createApolloClient } from '@atlassian/amkt-common';
import { ErrorBoundary, FeatureFlags, Router } from '@atlassian/mpac-ui';
import { createBrowserHistory } from 'history';
import Immutable from 'immutable';
import React from 'react';
import { ScreenClassProvider } from 'react-grid-system';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';

import IdentityHeartbeat from '../common/components/IdentityHeartBeat';
import ServerError from '../common/components/ServerError';
import Link from '../common/records/Link';
import { receiveAppConfig } from './actions/App';
import bootstrapConfig from './bootstrapConfig';
import AnalyticsListener from './components/common/AnalyticsListener';
import { QualtricsIntercept } from './components/common/QualtricsIntercept';
import Routes from './components/Routes';
import { resolveAddonDiscoveryConfig } from './records/AddonDiscoveryConfig';
import AppConfig from './records/AppConfig';
import { resolveCollectionsConfig } from './records/CollectionsConfig';
import { resolveCommonConfig } from './records/CommonConfig';
import { resolveGeneralLinks } from './records/GeneralLinks';
import { resolveSolutionsConfig } from './records/SolutionsConfig';
import { resolveVendorDetailsConfig } from './records/VendorDetailsConfig';
import configureStore from './store/configureStore';
import { GlobalStyles } from './styles/global';

import './app.global.less';

const history = createBrowserHistory();

function getStore(bootstrap) {
  const store = configureStore({ middleware: [thunk] });

  const appConfig = Immutable.fromJS(bootstrap.appConfig);

  store.dispatch(
    receiveAppConfig({
      appConfig: new AppConfig({
        commonConfig: resolveCommonConfig({ appConfig }),
        generalLinks: resolveGeneralLinks({ appConfig }),
        addonDiscovery: resolveAddonDiscoveryConfig({ appConfig }),
        collections: resolveCollectionsConfig({ appConfig }),
        solutions: resolveSolutionsConfig({ appConfig }),
        vendorDetails: resolveVendorDetailsConfig({ appConfig }),
      }),
    })
  );

  return store;
}

export default function getApp(bootstrap) {
  bootstrapConfig.set(bootstrap);
  const store = getStore(bootstrap);
  const apolloClient = createApolloClient({
    uri: bootstrap.graphQLGatewayUrl,
    name: 'marketplace-amkt-frontend',
    version: process.env.RELEASE_VERSION,
  });
  const homeLink = new Link({
    href: '/',
  });
  const appErrorFallback = <ServerError homeLink={homeLink} />;

  return (
    <ErrorBoundary fallback={appErrorFallback} boundaryName="App">
      <GlobalStyles />
      <Provider store={store}>
        <ApolloProvider client={apolloClient}>
          <FeatureFlags.Provider value={bootstrap.featureFlags}>
            <AnalyticsListener>
              <FlagsProvider>
                {bootstrap.enableIdentityHeartbeats && <IdentityHeartbeat />}
                <ScreenClassProvider>
                  <Router history={history}>
                    <Routes />
                  </Router>
                </ScreenClassProvider>
              </FlagsProvider>
            </AnalyticsListener>
            {bootstrap.featureFlags.showQualtricsSurvey && <QualtricsIntercept />}
          </FeatureFlags.Provider>
        </ApolloProvider>
      </Provider>
    </ErrorBoundary>
  );
}
